import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';

import { PieceProductSettings } from 'extensions/core/piece-product/front/models/piece-product.models';
import { ImageDetails } from 'extensions/core/piece-product/front/piece-product-render.component';
import { Product, ProductPartNumberDetails, ProductSize } from 'idt-ecommerce-api/catalog/products/products.models';
import { CMSEvent, CMSSelectedProductSizeEvent } from 'render/models/cms-event.model';
import * as ModalPanelActions from 'render/render-shared/ui/render-modal-panel/store/render-modal-panel.actions';
import { RenderState } from 'render/render.state';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';

import { RenderSelectComponent } from '../render-select/render-select.component';

import { templateUrl, styleUrls } from './render-product-purchase-options.component.paths';


@Component({
  selector: 'render-product-purchase-options',
  templateUrl,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenderProductPurchaseOptionsComponent {

  @ViewChild('sizeSelectorComponent') public sizeSelector: RenderSelectComponent | undefined;
  @Input() public product!: Product;
  @Input() public imageDetails: ImageDetails | undefined;
  @Input() public piece: IdtComponent<PieceProductSettings> | undefined;
  @Input() public country?: string;
  @Input() public lang?: string;
  @Input() public isModal?: boolean = false;
  public selectedSize?: string;

  constructor(
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    protected readonly renderConfigurationService: RenderConfigurationService,
    protected readonly store: Store<RenderState>
  ) {}

  public get hasToDisplayProductSizesSelector(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesSelector;
  }

  public get hasToDisplayProductSizesAdditionalInfo(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesAdditionalInfo;
  }

  public get hasToDisplayProductSizesLastUnitsWarning(): boolean {
    return !!this.piece?.settings?.general?.displayLastSizeUnitsWarning;
  }

  public get hasToDisplayViewSimilarProducts(): boolean {
    return !!this.piece?.settings?.general?.displayViewSimilar;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public onProductCartClick(size?: string): void {
    if (this.hasToDisplayProductSizesSelector && !this.selectedSize && !size) {
      this.sizeSelector?.toggle();
    } else {
      if (size) { this.onSizeSelected(size); }
      const cmsEvent: CMSEvent<Product> = {
        name: 'productCartClicked',
        type: 'click',
        data: {
          ...this.product,
          ...this.getProductPartsFromDetails()
        }
      };
      this.cmsRenderEventsService.emitEvent(cmsEvent);
    }
  }

  public onSizeSelected(size: string): void {
    this.selectedSize = size;

    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'productSizeSelected',
      type: 'click',
      data: {
        product: this.product,
        size,
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public onViewSimilarClick(size: string): void {
    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'viewSimilarProductsClicked',
      type: 'click',
      data: {
        product: this.product,
        size,
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public openSizeSelectorModal(): void {
    if (this.product.colors[0]?.sizes && this.product.colors[0].sizes.length === 1) {
      this.onProductCartClick(this.product.colors[0].sizes[0].name ?? 'M');
    } else {
      document.body.style.overflowY = 'hidden';
      this.store.dispatch(ModalPanelActions.openModal(
        {
          modalName: 'size-selector-modal',
          props: {
            data: {
              product: this.product,
              imageDetails: this.imageDetails,
              piece: this.piece,
              country: this.country,
              lang: this.lang
            }
          }
        }
      ));
    }
  }

  public trackBySize(index: number, size: ProductSize): string {
    return size.sku;
  }

  private getProductPartsFromDetails(): ProductPartNumberDetails | undefined {
    if (this.imageDetails?.colorId || this.imageDetails?.style) {
      return {
        ...(this.imageDetails?.colorId && { colorId: this.imageDetails.colorId }),
        ...(this.imageDetails?.style && { style: this.imageDetails.style })
      };
    }

    return undefined;
  }

}
